import { getCurrencyAdAccountPlatform } from "../../../actions/getters";
import { ADS_CAMPAIGN_STATUS_DRAFT } from "../../../utils/constants";

const EMPTY_TOTALS = {
  impressions: 0,
  clicks: 0,
  mainAction: 0,
  spend: {},
  spendByPlatform: {},
  roas: 0,
};

const resolveCurrency = ({ dispatch, platform, platformData, metrics }) => {
  const fromMetrics = metrics?.currency || metrics?.account_currency || null;
  if (fromMetrics) {
    return fromMetrics;
  }

  if (platformData?.integrationID && platformData?.adAccountID) {
    const currency = dispatch(
      getCurrencyAdAccountPlatform({
        platform,
        integrationID: platformData.integrationID,
        adAccountID: platformData.adAccountID,
      })
    );
    if (currency) {
      return currency;
    }
  }

  return "USD";
};

/**
 * Aggregates per-campaign statistics into totals compatible with CampaignStatisticsOverview.
 * @param {Object} params
 * @param {Object} params.campaigns - Campaign objects keyed by campaignID
 * @param {Object} params.statistics - Unified getUnifiedCampaignStatistics response data
 * @param {Function} params.dispatch - Redux dispatch
 * @returns {Object} totals
 */
export const aggregateCampaignStatisticsTotals = ({
  campaigns = {},
  statistics = {},
  dispatch,
}) => {
  const campaignsStats = statistics?.campaigns || {};

  if (!dispatch || !statistics || !Object.keys(campaignsStats).length) {
    return { ...EMPTY_TOTALS };
  }

  const totals = {
    impressions: 0,
    clicks: 0,
    mainAction: 0,
    spend: {},
    spendByPlatform: {},
  };
  const roasValues = [];

  Object.keys(campaigns).forEach((campaignID) => {
    const campaign = campaigns[campaignID];

    if (campaign?.states?.general === ADS_CAMPAIGN_STATUS_DRAFT) {
      return;
    }

    const unifiedCampaign = campaignsStats[campaignID];
    if (!unifiedCampaign) {
      return;
    }

    (unifiedCampaign.platforms || []).forEach((platform) => {
      const metrics =
        unifiedCampaign?.levels?.campaign?.maximum?.[platform]?.campaign;

      if (!metrics) {
        return;
      }

      const impressions = Number(metrics.impressions || 0);
      const clicks = Number(metrics.click || 0);
      const mainAction = Number(metrics.mainAction || 0);
      const spend = Number(metrics.spend || 0);
      const roas = Number(metrics.roas || 0);

      totals.impressions += impressions;
      totals.clicks += clicks;
      totals.mainAction += mainAction;

      if (roas > 0) {
        roasValues.push(roas);
      }

      if (spend > 0) {
        const platformData = campaign?.platformsData?.[platform] || {};
        const currency = resolveCurrency({
          dispatch,
          platform,
          platformData,
          metrics,
        });

        if (!totals.spend[currency]) {
          totals.spend[currency] = 0;
        }
        totals.spend[currency] += spend;

        if (!totals.spendByPlatform[platform]) {
          totals.spendByPlatform[platform] = {};
        }
        if (!totals.spendByPlatform[platform][currency]) {
          totals.spendByPlatform[platform][currency] = 0;
        }
        totals.spendByPlatform[platform][currency] += spend;
      }
    });
  });

  totals.roas =
    roasValues.length > 0
      ? roasValues.reduce((sum, value) => sum + value, 0) / roasValues.length
      : 0;

  return totals;
};

export default aggregateCampaignStatisticsTotals;
